<!--
  PoolaNextRunStrip — the slim bar that keeps the countdown to the NEXT Poola on
  screen while the member reads the last one (the last-Poola page). A CONTENT
  FRAGMENT: no fetch, no store, no router, never its own <f7-page>.

  Two forms, one root element each:
    • counting  → «البولة الجاية هتبدأ بعد» + days (hidden at 0) + HH:MM:SS.
    • open      → «البولة فتحت — ادخل دلوقتي» as a link to `href`, once the
                  countdown is past or the run is already ACTIVE (an early open
                  reaches the page as a status flip before the clock runs out).

  The target is captured ONCE at setup (useCountdown wants a fixed target at its
  synchronous call site): Date.parse(deal.startAt) − serverOffset, so it counts
  against SERVER time on a skewed phone. The parent mounts the strip only when
  the deal is loaded, and re-keys it if startAt ever changes.

  The link is a real <a href> with `prevent-router` and emits `open` instead of
  letting Framework7 push `href`: the page decides HOW to go (back when the
  Poola page is right behind it, otherwise in place of this entry). Pushing
  would stack Poola → last → Poola, and every «رجوع» after that would ping-pong.
  Same pattern as TadbeesaStrip.

  A plain block: it does not position itself. The page pins it under the navbar,
  outside the scroller (PoolaLastRunPage, with useFixedSummaryBar), so the
  countdown stays on screen while the offers scroll.
-->
<template>
  <a
    v-if="isOpen"
    :href="href"
    class="poola-next-strip poola-next-strip--open prevent-router"
    @click.prevent="emit('open')"
  >
    <span class="poola-next-strip__label">{{ t('poola.lastRun.nextIsOpen') }}</span>
    <svg
      class="poola-next-strip__chevron"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2.4"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  </a>

  <div v-else-if="hasTarget" class="poola-next-strip">
    <span class="poola-next-strip__label">{{ t('poola.lastRun.nextStartsIn') }}</span>
    <span class="poola-next-strip__time" role="timer">
      <span v-if="countdown.days > 0" class="poola-next-strip__days">
        {{ countdown.days }} {{ t('poola.countdown.days') }}
      </span>
      <!-- dir="ltr": hours → seconds read left-to-right in every locale. -->
      <span class="poola-next-strip__clock" dir="ltr">{{ clock }}</span>
    </span>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCountdown } from '@/composables/useCountdown.js';
import { POOLA_DEAL_STATUS } from '@/constants/poola.js';

const props = defineProps({
  // The CURRENT run (usePoolaDeal.deal) — the one that is about to start.
  deal: {
    type: Object,
    required: true,
  },
  // serverNow − clientNow (ms), captured once by usePoolaDeal. 0 = in sync.
  serverOffset: {
    type: Number,
    default: 0,
  },
  // The Poola page the "open now" link names.
  href: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['open']);

const { t } = useI18n({ useScope: 'global' });

// Offset-adjusted target, read ONCE (deliberately not reactive, like
// PoolaRunHeader's). null when startAt is missing or unparseable.
function captureTargetMs() {
  const raw = props.deal?.startAt;
  if (raw == null) return null;
  const ms = typeof raw === 'number' ? raw : Date.parse(raw);
  return Number.isFinite(ms) ? ms - props.serverOffset : null;
}
const targetMs = captureTargetMs();
const hasTarget = targetMs != null;

// No target → epoch 0: a past target makes the instance inert on its first
// tick (no idle 1s interval). `hasTarget` keeps that from reading as "open".
const countdown = useCountdown(targetMs ?? 0);

const isLive = computed(() => Number(props.deal?.status) === POOLA_DEAL_STATUS.ACTIVE);
const isOpen = computed(() => isLive.value || (hasTarget && countdown.isPast));

function pad(n) {
  return String(n).padStart(2, '0');
}
// Western digits by construction (plain JS numbers, never a locale formatter).
const clock = computed(
  () => `${pad(countdown.hours)}:${pad(countdown.minutes)}:${pad(countdown.seconds)}`,
);
</script>

<style scoped>
.poola-next-strip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  box-sizing: border-box;
  min-height: 44px;
  padding: 6px 16px;
  background: var(--poolit-primary-900);
  color: #fff;
  text-decoration: none;
}

.poola-next-strip__label {
  font-size: 0.875rem;
  font-weight: var(--poolit-font-weight-semibold);
  line-height: 20px;
}

.poola-next-strip__time {
  display: flex;
  flex: none;
  align-items: baseline;
  gap: 8px;
  font-size: 0.9375rem;
  font-weight: var(--poolit-font-weight-bold);
  line-height: 20px;
  font-variant-numeric: tabular-nums;
}

.poola-next-strip__clock {
  direction: ltr;
  unicode-bidi: isolate;
}

/* ── Open now ────────────────────────────────────────────────────────────── */
.poola-next-strip--open {
  justify-content: center;
  gap: 6px;
  min-height: 48px;
}

.poola-next-strip--open .poola-next-strip__label {
  font-size: 0.9375rem;
  font-weight: var(--poolit-font-weight-bold);
}

.poola-next-strip__chevron {
  flex: none;
}
</style>

<!-- Non-scoped block: a rule that must reach [dir='rtl'] on <html> (CLAUDE.md:
     never :global(ancestor) .descendant inside <style scoped>). The chevron is
     drawn pointing to the inline end: right in LTR, left in RTL. -->
<style>
[dir='rtl'] .poola-next-strip__chevron {
  transform: scaleX(-1);
}
</style>
